/**
 * ThemeContext.jsx — Phase 2 App Shell (Jul 2026)
 *
 * Lightweight class-based theme provider (no next-themes dependency).
 * Persists `light | dark | system` in localStorage under
 * `ankastra_theme` and toggles `<html class="dark">` accordingly.
 * The Tailwind config already uses `darkMode: ['class']` so this is
 * all that's needed.
 *
 * Jul 2026 — Phase 1 sidebar redesign: default changed from `system`
 * to `light`. One-time migration forces existing users to light on
 * first boot of this version (theme_version < 2), then respects user
 * toggles thereafter.
 *
 * Consumers:
 *   - <Logo/>          → swaps light/dark asset by resolved theme
 *   - AppTopbar profile menu → renders the toggle button
 *   - Any future component that wants an explicit `theme` read
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const THEME_KEY = 'ankastra_theme';
const THEME_VERSION_KEY = 'ankastra_theme_version';
const CURRENT_THEME_VERSION = 2;
const VALID_MODES = ['light', 'dark', 'system'];

const ThemeContext = createContext({
  theme:        'light', // user's setting
  resolvedTheme:'light',  // actual applied theme (system → light|dark)
  setTheme:     () => {},
  toggleTheme:  () => {},
  isMounted:    false,
});

function getSystemPref() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * One-time migration: if the stored theme version is older than
 * CURRENT_THEME_VERSION, force light mode and stamp the new version.
 * This ensures all users start on light mode exactly once after this
 * deploy, then their preference is respected going forward.
 */
function migrateThemeIfNeeded() {
  try {
    const storedVersion = parseInt(localStorage.getItem(THEME_VERSION_KEY) || '0', 10);
    if (storedVersion < CURRENT_THEME_VERSION) {
      localStorage.setItem(THEME_KEY, 'light');
      localStorage.setItem(THEME_VERSION_KEY, String(CURRENT_THEME_VERSION));
    }
  } catch { /* no-op in environments without localStorage */ }
}

function readStoredTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw && VALID_MODES.includes(raw)) return raw;
  } catch { /* no-op */ }
  return 'light';
}

function applyTheme(mode) {
  const resolved = mode === 'system' ? getSystemPref() : mode;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Also set `color-scheme` for form controls / scrollbars.
  root.style.colorScheme = resolved;
  return resolved;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState]      = useState('light');
  const [resolvedTheme, setResolved] = useState('light');
  const [isMounted, setIsMounted]   = useState(false);

  // Mount hydration — run migration, read stored + apply, then flip
  // the mount flag so components can render correctly without flash.
  useEffect(() => {
    migrateThemeIfNeeded();
    const initial = readStoredTheme();
    setThemeState(initial);
    setResolved(applyTheme(initial));
    setIsMounted(true);
  }, []);

  // Follow OS changes when theme === 'system'.
  useEffect(() => {
    if (theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(applyTheme('system'));
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);

  const setTheme = useCallback((mode) => {
    if (!VALID_MODES.includes(mode)) return;
    setThemeState(mode);
    setResolved(applyTheme(mode));
    try { localStorage.setItem(THEME_KEY, mode); } catch { /* no-op */ }
  }, []);

  const toggleTheme = useCallback(() => {
    // Cycle: light → dark → system → light …
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme, isMounted }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
